import {
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faPen
} from "@fortawesome/free-solid-svg-icons"
import { CSSProperties, DragEvent, useEffect, useMemo, useRef, useState } from "react"

import ListButton from "@/components/list-button"
import Chip from "@/components/chip"
import LaunchButton from "@/components/LaunchButton"
import Modal from "@/components/modal"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import RiskWizardOverview from "@/entities/physical-risk/ui/RiskWizardOverview"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { PreventionAction } from "@/entities/prevention-action/model/prevention-action.types"
import PreventionActionCalendarItem from "@/entities/prevention-action/ui/PreventionActionCalendarItem"
import PreventionActionAddModal from "@/entities/prevention-action/ui/modal/PreventionActionAddModal"
import getColor from "@/utils/get-color.utils"

const MONTH_COUNT = 12
const MONTHS_PER_ROW = 4
const MAX_TRACKS_PER_ROW = 4
const MONTH_HEADER_HEIGHT = 24
const ACTION_HEIGHT = 42
const ACTION_GAP = 10
const ROW_COUNT = MONTH_COUNT / MONTHS_PER_ROW

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1)
}

function addMonthsPreservingDay(date: Date, amount: number) {
  const targetMonth = new Date(date.getFullYear(), date.getMonth() + amount, 1)
  const lastDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate()
  return new Date(
    targetMonth.getFullYear(),
    targetMonth.getMonth(),
    Math.min(date.getDate(), lastDay),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  )
}

function getMonthDifference(from: Date, to: Date) {
  return (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth()
}

function getColumnFromPointer(event: DragEvent<HTMLElement>) {
  const bounds = event.currentTarget.getBoundingClientRect()
  return Math.min(
    MONTHS_PER_ROW - 1,
    Math.max(0, Math.floor(((event.clientX - bounds.left) / bounds.width) * MONTHS_PER_ROW))
  )
}

function formatMonth(date: Date) {
  const label = date.toLocaleDateString("fr-FR", { month: "long" })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function isActionInMonth(action: PreventionAction, month: Date) {
  const endDate = action.cStatus === "done" ? (action.dateDone ?? action.datePlanned) : action.datePlanned
  if (!endDate) return false

  const startDate = action.cStatus === "done" ? (action.dateStarted ?? endDate) : (action.dateStartedPlanned ?? endDate)
  const end = startOfMonth(new Date(endDate))
  const start = startOfMonth(new Date(startDate))
  return month >= start && month <= end
}

//Le calcul des dates est partage entre l'apercu pendant le drag et l'enregistrement au drop :
//les deux doivent donner exactement le meme resultat. null = drag sans effet (ou invalide).
function getDatesAfterDrag(action: PreventionAction, dragMode: DragMode, targetMonth: Date) {
  if (dragMode === "schedule") return { dateStartedPlanned: null, datePlanned: targetMonth }
  if (!action.datePlanned) return null

  const endDate = new Date(action.datePlanned)
  const startDate = new Date(action.dateStartedPlanned ?? action.datePlanned)

  if (dragMode === "move") {
    const monthDelta = getMonthDifference(startOfMonth(startDate), targetMonth)
    if (monthDelta === 0) return null
    return {
      dateStartedPlanned: action.dateStartedPlanned ? addMonthsPreservingDay(startDate, monthDelta) : null,
      datePlanned: addMonthsPreservingDay(endDate, monthDelta)
    }
  }

  const resizedDate = dragMode === "resize-start" ? startDate : endDate
  const monthDelta = getMonthDifference(startOfMonth(resizedDate), targetMonth)
  if (monthDelta === 0) return null
  const targetDate = addMonthsPreservingDay(resizedDate, monthDelta)

  if (dragMode === "resize-start") {
    if (startOfMonth(targetDate) > startOfMonth(endDate)) return null
    return { dateStartedPlanned: targetDate, datePlanned: endDate }
  }
  if (startOfMonth(targetDate) < startOfMonth(startDate)) return null
  return { dateStartedPlanned: startDate, datePlanned: targetDate }
}

interface CalendarSegment {
  action: CalendarAction
  columnEnd: number
  columnStart: number
  track: number
}

interface CalendarAction extends PreventionAction {
  calendarSourceAction?: PreventionAction
  isPeriodicOccurrence?: boolean
}

type DragMode = "move" | "resize-start" | "resize-end" | "schedule"

function createRowSegments(actions: CalendarAction[], months: Date[], rowIndex: number) {
  const rowMonths = months.slice(rowIndex * MONTHS_PER_ROW, rowIndex * MONTHS_PER_ROW + MONTHS_PER_ROW)
  const segments = actions
    .map((action) => {
      const coveredColumns = rowMonths
        .map((month, column) => (isActionInMonth(action, month) ? column : -1))
        .filter((column) => column >= 0)
      if (!coveredColumns.length) return null
      return {
        action,
        columnStart: Math.min(...coveredColumns),
        columnEnd: Math.max(...coveredColumns),
        track: 0
      }
    })
    .filter((segment): segment is CalendarSegment => Boolean(segment))
    .sort((a, b) => a.columnStart - b.columnStart || b.columnEnd - a.columnEnd)

  const trackEnds: number[] = []
  return segments.map((segment) => {
    const freeTrack = trackEnds.findIndex((end) => end < segment.columnStart)
    segment.track = freeTrack === -1 ? trackEnds.length : freeTrack
    trackEnds[segment.track] = segment.columnEnd
    return segment
  })
}

function distributeTrackCapacity(rowDemands: number[], availableTracks: number) {
  const capacities = rowDemands.map(() => 1)
  let remainingTracks = Math.max(0, availableTracks - capacities.length)

  for (let level = 2; level <= MAX_TRACKS_PER_ROW && remainingTracks > 0; level += 1) {
    rowDemands
      .map((demand, rowIndex) => ({ demand, rowIndex }))
      .filter(({ demand }) => demand >= level)
      .sort((a, b) => b.demand - a.demand)
      .forEach(({ rowIndex }) => {
        if (remainingTracks > 0) {
          capacities[rowIndex] = level
          remainingTracks -= 1
        }
      })
  }

  return capacities
}

export default function PreventionActionsCalendar() {
  const dispatch = useAppDispatch()
  const actions = useAppSelector(preventionActionStore.selectors.list)
  const risks = useAppSelector(physicalRiskStore.selectors.list)
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()))
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null)
  const [selectedAction, setSelectedAction] = useState<PreventionAction | null>(null)
  const [editingRisk, setEditingRisk] = useState<PhysicalRisk | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [draggedAction, setDraggedAction] = useState<PreventionAction | null>(null)
  const [dragMode, setDragMode] = useState<DragMode | null>(null)
  const [dropMonth, setDropMonth] = useState<Date | null>(null)
  const [availableTracks, setAvailableTracks] = useState(ROW_COUNT * MAX_TRACKS_PER_ROW)
  const calendarGridRef = useRef<HTMLDivElement>(null)
  const origin = currentMonth
  const currentYear = new Date().getFullYear()
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1]
  const selectedRisk = selectedAction ? (risks.find((risk) => risk.id === selectedAction.PhysicalRiskId) ?? null) : null
  const months = useMemo(
    () => Array.from({ length: MONTH_COUNT }, (_, index) => addMonths(origin, index)),
    [origin.getTime()]
  )
  const calendarActions = useMemo<CalendarAction[]>(() => {
    const periodicOccurrences = actions
      .filter((action) => action.dateDone && action.refreshDelay)
      .map((action) => {
        const occurrenceDate = addMonthsPreservingDay(new Date(action.dateDone!), action.refreshDelay!)
        return {
          ...action,
          id: `${action.id}-periodic-${occurrenceDate.getTime()}`,
          cStatus: "todo" as const,
          dateDone: null,
          dateStarted: null,
          dateStartedPlanned: occurrenceDate,
          datePlanned: occurrenceDate,
          calendarSourceAction: action,
          isPeriodicOccurrence: true
        }
      })

    return [...actions, ...periodicOccurrences]
  }, [actions])
  const displayedCalendarActions = useMemo(
    () =>
      draggedAction && dragMode && dropMonth
        ? calendarActions.map((action) =>
            action.id === draggedAction.id ? { ...action, ...getDatesAfterDrag(action, dragMode, dropMonth) } : action
          )
        : calendarActions,
    [calendarActions, dragMode, draggedAction, dropMonth]
  )
  const rowDemands = useMemo(
    () =>
      Array.from({ length: ROW_COUNT }, (_, rowIndex) => {
        const segments = createRowSegments(displayedCalendarActions, months, rowIndex)
        return Math.min(MAX_TRACKS_PER_ROW, Math.max(1, ...segments.map((segment) => segment.track + 1)))
      }),
    [displayedCalendarActions, months]
  )
  const rowTrackCapacities = useMemo(
    () => distributeTrackCapacity(rowDemands, availableTracks),
    [availableTracks, rowDemands]
  )

  useEffect(() => {
    const calendarGrid = calendarGridRef.current
    if (!calendarGrid) return

    const updateAvailableTracks = (height: number) => {
      const measuredTracks = Math.floor(
        (height - ROW_COUNT * (MONTH_HEADER_HEIGHT + ACTION_GAP)) / (ACTION_HEIGHT + ACTION_GAP)
      )
      setAvailableTracks(Math.min(ROW_COUNT * MAX_TRACKS_PER_ROW, Math.max(ROW_COUNT, measuredTracks)))
    }
    const resizeObserver = new ResizeObserver(([entry]) => updateAvailableTracks(entry.contentRect.height))

    updateAvailableTracks(calendarGrid.getBoundingClientRect().height)
    resizeObserver.observe(calendarGrid)
    return () => resizeObserver.disconnect()
  }, [selectedMonth])

  useEffect(() => {
    const resetDrag = () => {
      setDraggedAction(null)
      setDragMode(null)
      setDropMonth(null)
    }
    window.addEventListener("dragend", resetDrag)
    return () => window.removeEventListener("dragend", resetDrag)
  }, [])

  function getMonthActions(month: Date) {
    return displayedCalendarActions.filter((action) => isActionInMonth(action, month))
  }

  function openAction(action: PreventionAction) {
    if (action.cStatus === "done") {
      editAction(action)
      return
    }
    setSelectedAction(action)
  }

  function editAction(action: PreventionAction) {
    dispatch(preventionActionStore.actions.setActive(action))
    setSelectedAction(null)
    setIsEditModalOpen(true)
  }

  async function markActionAsDone(action: PreventionAction) {
    dispatch(preventionActionStore.actions.setActive(action))
    await dispatch(preventionActionStore.thunks.updateStatus({ status: "done" })).unwrap()
    setSelectedAction(null)
  }

  async function applyDragToAction(action: PreventionAction, mode: DragMode, targetMonth: Date) {
    const dates = getDatesAfterDrag(action, mode, targetMonth)
    if (!dates) return

    dispatch(preventionActionStore.actions.setActive(action))
    dispatch(preventionActionStore.actions.edit({ key: "dateStartedPlanned", value: dates.dateStartedPlanned }))
    dispatch(preventionActionStore.actions.edit({ key: "datePlanned", value: dates.datePlanned }))
    await dispatch(preventionActionStore.thunks.updateEntity()).unwrap()
  }

  function activateExternalDrag(event: DragEvent<HTMLElement>) {
    if (draggedAction) return
    const actionId = event.dataTransfer.getData("application/x-prevention-action-id")
    const action = actions.find((item) => item.id === actionId)
    if (!action) return
    setDraggedAction(action)
    setDragMode("schedule")
  }

  async function dropActionOnMonth(targetMonth: Date, event: DragEvent<HTMLElement>) {
    const actionId = event.dataTransfer.getData("application/x-prevention-action-id")
    const scheduledAction = actions.find((item) => item.id === actionId)
    if (scheduledAction) {
      await applyDragToAction(scheduledAction, "schedule", targetMonth)
      return
    }
    if (draggedAction && dragMode) await applyDragToAction(draggedAction, dragMode, targetMonth)
  }

  function renderAction(
    action: CalendarAction,
    {
      isDetailed = false,
      calendarStyle,
      key = action.id,
      showStartResizeHandle = false,
      showEndResizeHandle = false
    }: {
      isDetailed?: boolean
      calendarStyle?: CSSProperties
      key?: string
      showEndResizeHandle?: boolean
      showStartResizeHandle?: boolean
    } = {}
  ) {
    const canChangeDates = !isDetailed && action.cStatus !== "done" && action.refreshDelay === null
    return (
      <PreventionActionCalendarItem
        key={key}
        action={action}
        className={isDetailed ? "" : "min-h-0 self-center"}
        draggable={canChangeDates}
        showStartResizeHandle={canChangeDates && showStartResizeHandle}
        showEndResizeHandle={canChangeDates && showEndResizeHandle}
        style={calendarStyle}
        onDragStart={(event) => {
          event.stopPropagation()
          event.dataTransfer.effectAllowed = "move"
          setDraggedAction(action)
          setDragMode("move")
        }}
        onResizeStartStart={(event) => {
          event.stopPropagation()
          event.dataTransfer.effectAllowed = "move"
          setDraggedAction(action)
          setDragMode("resize-start")
        }}
        onResizeEndStart={(event) => {
          event.stopPropagation()
          event.dataTransfer.effectAllowed = "move"
          setDraggedAction(action)
          setDragMode("resize-end")
        }}
        onDragEnd={() => {
          setDraggedAction(null)
          setDragMode(null)
          setDropMonth(null)
        }}
        onClick={(event) => {
          event.stopPropagation()
          openAction(action.calendarSourceAction ?? action)
        }}
      />
    )
  }

  //La visibilite vient de l'assignation des pistes, pas de l'ordre du store : on soustrait donc
  //les segments reellement rendus sur cette colonne
  function renderMonthSummary(
    month: Date,
    monthActions: CalendarAction[],
    column: number,
    trackCapacity: number,
    visibleSegments: CalendarSegment[]
  ) {
    const visibleActionIds = new Set(
      visibleSegments
        .filter((segment) => column >= segment.columnStart && column <= segment.columnEnd)
        .map((segment) => segment.action.id)
    )
    const hiddenActions = monthActions.filter((action) => !visibleActionIds.has(action.id))
    if (!hiddenActions.length) return null

    return (
      <PreventionActionCalendarItem
        key={`summary-${month.getTime()}`}
        action={hiddenActions[0]}
        className="min-h-0 self-start"
        displayPeriodicStyle={false}
        displayStatus={false}
        displaySubtitle={false}
        title={`${hiddenActions.length} autres mesures`}
        titleClassName="font-normal"
        style={{
          backgroundColor: "#334155",
          color: "white",
          gridColumn: column + 1,
          gridRow: trackCapacity,
          margin: "0 10px",
          zIndex: 3
        }}
        onClick={(event) => {
          event.stopPropagation()
          setSelectedMonth(month)
        }}
      />
    )
  }

  const content = selectedMonth ? (
    (() => {
      const monthActions = getMonthActions(selectedMonth)
      return (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-[18px] flex items-center gap-[14px]">
            <ListButton icon={faChevronLeft} onClick={() => setSelectedMonth(null)} />
            <div className="text-lg">
              {formatMonth(selectedMonth)} {selectedMonth.getFullYear()}
            </div>
            <div className="text-sm text-slate-500">{monthActions.length} mesures de prévention</div>
          </div>
          <div className="card mb-[30px] min-h-0 flex-1 overflow-y-auto p-[20px]">
            <div className="flex flex-col gap-[10px]">
              {monthActions.map((action) => renderAction(action, { isDetailed: true }))}
            </div>
          </div>
        </div>
      )
    })()
  ) : (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="card mb-20 flex min-h-[420px] flex-1 flex-col overflow-hidden">
        <div
          className="flex shrink-0 items-center justify-center gap-10 px-10 py-10"
          style={{ borderBottom: "1px solid #E2E8F0" }}>
          <ListButton icon={faAnglesLeft} onClick={() => setCurrentMonth(addMonths(currentMonth, -12))} />
          <ListButton icon={faChevronLeft} onClick={() => setCurrentMonth(addMonths(currentMonth, -1))} />
          <div className="flex gap-10">
            {yearOptions.map((year) => (
              <Chip
                key={year}
                color={getColor("grey")}
                isOutlineColored={currentMonth.getMonth() !== 0 || currentMonth.getFullYear() !== year}
                onClick={() => {
                  const isCurrentYearActive =
                    year === currentYear && currentMonth.getFullYear() === year && currentMonth.getMonth() === 0
                  setCurrentMonth(isCurrentYearActive ? startOfMonth(new Date()) : new Date(year, 0, 1))
                }}>
                {year}
              </Chip>
            ))}
          </div>
          <ListButton icon={faChevronRight} onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} />
          <ListButton icon={faAnglesRight} onClick={() => setCurrentMonth(addMonths(currentMonth, 12))} />
        </div>

        <div
          ref={calendarGridRef}
          className="grid min-h-0 flex-1"
          style={{
            gridTemplateRows: rowDemands
              .map((rowDemand, rowIndex) => {
                const usedTracks = Math.min(rowTrackCapacities[rowIndex], rowDemand)
                return `${MONTH_HEADER_HEIGHT + usedTracks * ACTION_HEIGHT + (usedTracks + 1) * ACTION_GAP}fr`
              })
              .join(" ")
          }}>
          {Array.from({ length: ROW_COUNT }, (_, rowIndex) => {
            const rowMonths = months.slice(rowIndex * MONTHS_PER_ROW, rowIndex * MONTHS_PER_ROW + MONTHS_PER_ROW)
            const segments = createRowSegments(displayedCalendarActions, months, rowIndex)
            const monthActions = rowMonths.map(getMonthActions)
            const trackCapacity = rowTrackCapacities[rowIndex]
            const crowdedColumns = new Set(
              monthActions
                .map((items, column) => (items.length > trackCapacity ? column : -1))
                .filter((column) => column >= 0)
            )
            const visibleSegments = segments.filter(
              (segment) =>
                segment.track < trackCapacity - 1 ||
                (segment.track === trackCapacity - 1 &&
                  !Array.from(crowdedColumns).some(
                    (column) => column >= segment.columnStart && column <= segment.columnEnd
                  ))
            )
            const usedTracks = Math.min(trackCapacity, Math.max(1, ...segments.map((segment) => segment.track + 1)))

            return (
              <div
                key={rowIndex}
                className="relative grid min-h-0 grid-rows-[24px_minmax(0,1fr)] overflow-hidden"
                style={{ borderBottom: rowIndex < 2 ? "1px solid #E2E8F0" : undefined }}>
                <div className="pointer-events-none absolute inset-y-0 left-1/4 z-[2] w-px bg-slate-200" />
                <div className="pointer-events-none absolute inset-y-0 left-1/2 z-[2] w-px bg-slate-200" />
                <div className="pointer-events-none absolute inset-y-0 left-3/4 z-[2] w-px bg-slate-200" />
                <div className="grid grid-cols-4">
                  {rowMonths.map((month) => {
                    return (
                      <div
                        key={month.getTime()}
                        className="_hover py-[2px] text-center text-sm text-slate-500"
                        onClick={() => setSelectedMonth(month)}>
                        <span>
                          {formatMonth(month)} {month.getFullYear()}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <div
                  className="relative grid min-h-0 grid-cols-4 content-start gap-y-10 overflow-hidden py-10"
                  style={{ gridTemplateRows: `repeat(${usedTracks}, ${ACTION_HEIGHT}px)` }}
                  onDragOver={(event) => {
                    event.preventDefault()
                    activateExternalDrag(event)
                    event.dataTransfer.dropEffect = "move"
                    setDropMonth(rowMonths[getColumnFromPointer(event)])
                  }}
                  onDrop={async (event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    await dropActionOnMonth(rowMonths[getColumnFromPointer(event)], event)
                    setDraggedAction(null)
                    setDragMode(null)
                    setDropMonth(null)
                  }}>
                  {rowMonths.map((month, column) => (
                    <div
                      key={`drop-${month.getTime()}`}
                      className="z-0 col-span-1"
                      style={{
                        gridColumn: column + 1,
                        gridRow: `1 / ${usedTracks + 1}`,
                        backgroundColor: dropMonth?.getTime() === month.getTime() ? "#F1F5FF" : undefined
                      }}
                      onDragEnter={() => setDropMonth(month)}
                      onDragOver={(event) => {
                        event.preventDefault()
                        activateExternalDrag(event)
                        event.dataTransfer.dropEffect = "move"
                      }}
                      onDrop={async (event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        await dropActionOnMonth(month, event)
                        setDraggedAction(null)
                        setDragMode(null)
                        setDropMonth(null)
                      }}
                      onClick={() => setSelectedMonth(month)}
                    />
                  ))}
                  {visibleSegments.map((segment) => {
                    const actionStart = startOfMonth(
                      new Date(segment.action.dateStartedPlanned ?? segment.action.datePlanned!)
                    )
                    const actionEnd = startOfMonth(new Date(segment.action.datePlanned!))
                    return renderAction(segment.action, {
                      calendarStyle: {
                        gridColumn: `${segment.columnStart + 1} / ${segment.columnEnd + 2}`,
                        gridRow: segment.track + 1,
                        margin: "0 10px",
                        zIndex: 3
                      },
                      key: `${segment.action.id}-${rowIndex}`,
                      showStartResizeHandle: rowMonths[segment.columnStart].getTime() === actionStart.getTime(),
                      showEndResizeHandle: rowMonths[segment.columnEnd].getTime() === actionEnd.getTime()
                    })
                  })}
                  {monthActions.map((items, column) =>
                    items.length > trackCapacity
                      ? renderMonthSummary(rowMonths[column], items, column, trackCapacity, visibleSegments)
                      : null
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {selectedAction && (
        <Modal
          isCloseButtonVisible
          isTabletFullScreenDisabled
          title="Mettre à jour la mesure de prévention"
          onClose={() => setSelectedAction(null)}>
          <div className="mb-20 text-lg">{selectedAction.name}</div>
          {selectedRisk && (
            <RiskWizardOverview
              risk={selectedRisk}
              onClick={() => {
                setSelectedAction(null)
                setEditingRisk(selectedRisk)
              }}
            />
          )}
          <div className="flex gap-20">
            <LaunchButton
              description="Confirmer que cette mesure est désormais appliquée"
              icon={faCircleCheck}
              iconBackground="#0E5AA7"
              isCta
              title="Confirmer la mise en place"
              onClick={() => markActionAsDone(selectedAction)}
            />
            <LaunchButton
              description="Mettre à jour ses informations, ses dates ou son périmètre"
              icon={faPen}
              title="Modifier la mesure"
              onClick={() => editAction(selectedAction)}
            />
          </div>
        </Modal>
      )}
      {editingRisk && <PhysicalRiskEditModal risk={editingRisk} onClose={() => setEditingRisk(null)} />}
      {isEditModalOpen && <PreventionActionAddModal isEditing onClose={() => setIsEditModalOpen(false)} />}
      {content}
    </>
  )
}
